import { localStorageProvider } from "../../../AppController";
import { parseJwt } from "../../../util/jwt";
import type {
  ISsoAuthPreFlightRequest,
  ISsoRepository,
} from "../../adapters/driven/repositories/ssoRepository";
import { clearMsalCache } from "../../adapters/driven/providers/msalProvider";

// Flag to prevent concurrent refresh attempts
let isRefreshingToken = false;
let refreshPromise: Promise<ISsoRefreshResult | null> | null = null;

// Buffer time before expiration to trigger refresh (5 minutes in seconds)
const TOKEN_REFRESH_BUFFER_SECONDS = 300;

// Debug logging function - no-op in production
const ssoDebug = (..._args: unknown[]) => {
  // Debug logging disabled
};

// SSO Repository reference - set after initialization to break circular dependency
let ssoRepository: ISsoRepository | null = null;

// Auth Provider reference for auto-refresh - set after initialization
let authProviderRef: {
  ssoLogin: (params: { token: string; expires_at: number }) => Promise<unknown>;
} | null = null;

// Amplify Provider reference for switching config during token refresh
let amplifyProviderRef: {
  amplifyConfigure: (type?: string) => unknown;
} | null = null;

/**
 * Sets the auth provider reference for auto-refresh operations.
 * Must be called after AppController initialization.
 */
export const setAuthProviderForAutoRefresh = (provider: {
  ssoLogin: (params: { token: string; expires_at: number }) => Promise<unknown>;
}) => {
  authProviderRef = provider;
};

/**
 * Sets the amplify provider reference for config switching during refresh.
 * Must be called after AppController initialization.
 */
export const setAmplifyProviderForAutoRefresh = (provider: {
  amplifyConfigure: (type?: string) => unknown;
}) => {
  amplifyProviderRef = provider;
};

// Auto-refresh timer reference
let autoRefreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

// Retry configuration for network failures
let refreshRetryCount = 0;
const MAX_REFRESH_RETRIES = 10;
const RETRY_DELAYS_SECONDS = [30, 60, 120, 300, 300, 300, 300, 300, 300, 300]; // 30s, 1m, 2m, then 5m intervals

/**
 * Schedules automatic token refresh before expiration
 * Should be called after successful SSO login or token refresh
 */
export const scheduleTokenAutoRefresh = () => {
  // Clear any existing scheduled refresh
  cancelScheduledRefresh();

  const idToken = localStorageProvider.get("idToken");
  const refreshToken = localStorageProvider.get("refreshToken");
  const sso = localStorageProvider.get("sso");

  if (sso !== "true" || !idToken || !refreshToken) {
    ssoDebug(
      "⏰ [Auto-Refresh] Not scheduling - SSO not active or missing tokens"
    );
    return;
  }

  try {
    const tokenData = parseJwt(idToken);
    const currentTime = Math.floor(Date.now() / 1000);
    const expiresAt = tokenData.exp || 0;
    const userId = tokenData.sub;

    if (!userId) {
      ssoDebug("⏰ [Auto-Refresh] Not scheduling - no userId in token");
      return;
    }

    // Calculate when to refresh (buffer time before expiry)
    const refreshAtTime = expiresAt - TOKEN_REFRESH_BUFFER_SECONDS;
    const delaySeconds = refreshAtTime - currentTime;

    if (delaySeconds <= 0) {
      // Token already needs refresh - do it immediately
      ssoDebug("⏰ [Auto-Refresh] Token needs immediate refresh");
      performAutoRefresh(refreshToken, userId);
      return;
    }

    const delayMs = delaySeconds * 1000;
    const refreshAtDate = new Date(refreshAtTime * 1000);

    ssoDebug(
      "⏰ [Auto-Refresh] Scheduled for:",
      refreshAtDate.toLocaleTimeString()
    );
    ssoDebug(
      "⏰ [Auto-Refresh] Delay:",
      `${Math.floor(delaySeconds / 60)}m ${delaySeconds % 60}s`
    );

    autoRefreshTimeoutId = setTimeout(() => {
      ssoDebug("⏰ [Auto-Refresh] Timer triggered - starting refresh...");
      performAutoRefresh(refreshToken, userId);
    }, delayMs);
  } catch (error) {
    console.error("[Auto-Refresh] Failed to schedule:", error);
  }
};

/**
 * Schedules a retry for token refresh with exponential backoff
 */
const scheduleRefreshRetry = (refreshToken: string, userId: string) => {
  if (refreshRetryCount >= MAX_REFRESH_RETRIES) {
    ssoDebug(
      "❌ [Auto-Refresh] Max retries reached, giving up. User will need to re-login if token expires."
    );
    refreshRetryCount = 0;
    return;
  }

  const delaySeconds =
    RETRY_DELAYS_SECONDS[
      Math.min(refreshRetryCount, RETRY_DELAYS_SECONDS.length - 1)
    ];
  refreshRetryCount++;

  ssoDebug(
    `🔄 [Auto-Refresh] Network/API error - scheduling retry ${refreshRetryCount}/${MAX_REFRESH_RETRIES} in ${delaySeconds}s`
  );

  autoRefreshTimeoutId = setTimeout(() => {
    ssoDebug(`🔄 [Auto-Refresh] Retry ${refreshRetryCount} triggered...`);
    performAutoRefresh(refreshToken, userId);
  }, delaySeconds * 1000);
};

/**
 * Performs the automatic token refresh
 * Note: This is called via setTimeout, so refreshSsoTokens will be defined by execution time
 */
const performAutoRefresh = async (refreshToken: string, userId: string) => {
  ssoDebug("🔄 [Auto-Refresh] Performing automatic token refresh...");

  // Check if we're online first
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    ssoDebug("📡 [Auto-Refresh] Network offline - scheduling retry...");
    scheduleRefreshRetry(refreshToken, userId);
    return;
  }

  // Switch to SSO identity pool config for the entire refresh flow
  // (both the API call and the federatedSignIn need the SSO identity pool)
  if (amplifyProviderRef) {
    const type =
      localStorageProvider.get("microsoftSso") === "true"
        ? "microsoft_sso"
        : "sso";
    amplifyProviderRef.amplifyConfigure(type);
  }

  try {
    // refreshSsoTokens is defined later in the file but will be available when this runs via setTimeout
    const result = await refreshSsoTokens(refreshToken, userId);

    if (result) {
      // Reset retry count on success
      refreshRetryCount = 0;

      ssoDebug(
        "✅ [Auto-Refresh] Success! New token expires at:",
        new Date(result.expiresAt * 1000).toLocaleTimeString()
      );

      // Re-authenticate with Amplify using the stored reference
      if (authProviderRef) {
        try {
          await authProviderRef.ssoLogin({
            token: result.idToken,
            expires_at: result.expiresAt,
          });
          ssoDebug("🎉 [Auto-Refresh] Amplify re-authentication successful!");
        } catch (error) {
          console.error(
            "[Auto-Refresh] Failed to re-authenticate with Amplify:",
            error
          );
        }
      } else {
        console.error(
          "[Auto-Refresh] authProviderRef not set - cannot re-authenticate"
        );
      }

      // Schedule next refresh
      scheduleTokenAutoRefresh();
    } else {
      ssoDebug("❌ [Auto-Refresh] Failed - scheduling retry...");
      // Schedule a retry instead of giving up
      scheduleRefreshRetry(refreshToken, userId);
    }
  } finally {
    // Always switch back to default config
    if (amplifyProviderRef) {
      amplifyProviderRef.amplifyConfigure();
    }
  }
};

/**
 * Cancels any scheduled token refresh
 * Should be called on logout
 */
export const cancelScheduledRefresh = () => {
  if (autoRefreshTimeoutId) {
    ssoDebug("⏰ [Auto-Refresh] Cancelling scheduled refresh");
    clearTimeout(autoRefreshTimeoutId);
    autoRefreshTimeoutId = null;
  }
  // Reset retry count when cancelling
  refreshRetryCount = 0;
};

// Network reconnection handler - attempt refresh when coming back online
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    const sso = localStorage.getItem("sso");
    const refreshToken = localStorage.getItem("refreshToken");
    const idToken = localStorage.getItem("idToken");

    if (sso === "true" && refreshToken && idToken) {
      ssoDebug(
        "📡 [Auto-Refresh] Network came back online - checking token status..."
      );

      try {
        const tokenData = parseJwt(idToken);
        const currentTime = Math.floor(Date.now() / 1000);
        const isExpiredOrExpiring =
          tokenData.exp &&
          tokenData.exp < currentTime + TOKEN_REFRESH_BUFFER_SECONDS;

        if (isExpiredOrExpiring) {
          ssoDebug("📡 [Auto-Refresh] Token needs refresh - triggering now...");
          // Reset retry count since we're trying fresh after reconnection
          refreshRetryCount = 0;
          scheduleTokenAutoRefresh();
        } else {
          ssoDebug(
            "📡 [Auto-Refresh] Token still valid, no immediate action needed"
          );
        }
      } catch (error) {
        console.error(
          "[Auto-Refresh] Error checking token on reconnection:",
          error
        );
      }
    }
  });
}

// =============================================================================
// TEST HELPERS - Call from browser console: window.SSO_TEST.forceRefresh()
// =============================================================================
if (typeof window !== "undefined") {
  (window as any).SSO_TEST = {
    /**
     * Force refresh the SSO token immediately (for testing)
     * Usage: window.SSO_TEST.forceRefresh()
     */
    forceRefresh: async () => {
      const refreshToken = localStorage.getItem("refreshToken");
      const idToken = localStorage.getItem("idToken");
      if (!refreshToken || !idToken) {
        return null;
      }
      const tokenData = parseJwt(idToken);
      const userId = tokenData.sub;
      if (!userId) {
        return null;
      }
      return await refreshSsoTokens(refreshToken, userId);
    },

    /**
     * Show current token status
     * Usage: window.SSO_TEST.status()
     */
    status: () => {
      const idToken = localStorage.getItem("idToken");
      const refreshToken = localStorage.getItem("refreshToken");
      const sso = localStorage.getItem("sso");

      if (!idToken) {
        return null;
      }

      try {
        const tokenData = parseJwt(idToken);
        const currentTime = Math.floor(Date.now() / 1000);
        const expiresAt = tokenData.exp || 0;
        const timeUntilExpiry = expiresAt - currentTime;

        return {
          ssoMode: sso === "true",
          hasIdToken: !!idToken,
          hasRefreshToken: !!refreshToken,
          expiresAt: new Date(expiresAt * 1000).toISOString(),
          currentTime: new Date(currentTime * 1000).toISOString(),
          expiresInSeconds: timeUntilExpiry,
          expiresIn:
            timeUntilExpiry > 0
              ? `${Math.floor(timeUntilExpiry / 60)}m ${timeUntilExpiry % 60}s`
              : "EXPIRED",
          isExpired: timeUntilExpiry <= 0,
          needsRefresh: timeUntilExpiry <= TOKEN_REFRESH_BUFFER_SECONDS,
          bufferSeconds: TOKEN_REFRESH_BUFFER_SECONDS,
        };
      } catch {
        return null;
      }
    },

    /**
     * Simulate an expired token by setting a fake short expiration
     * Usage: window.SSO_TEST.simulateExpiry()
     */
    simulateExpiry: () => {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) {
        return;
      }
      const parts = idToken.split(".");
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        payload.exp = Math.floor(Date.now() / 1000) + 1;
        const newPayload = btoa(JSON.stringify(payload));
        const fakeToken = `${parts[0]}.${newPayload}.${parts[2]}`;
        localStorage.setItem("idToken", fakeToken);
      }
    },

    /**
     * Clear all SSO data and logout
     * Usage: window.SSO_TEST.clearAndLogout()
     */
    clearAndLogout: () => {
      cancelScheduledRefresh();
      localStorage.removeItem("sso");
      localStorage.removeItem("idToken");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.replace(window.location.origin + "/#/logout");
    },

    /**
     * Schedule automatic token refresh (for testing)
     * Usage: window.SSO_TEST.scheduleAutoRefresh()
     */
    scheduleAutoRefresh: () => {
      scheduleTokenAutoRefresh();
    },

    /**
     * Cancel scheduled token refresh (for testing)
     * Usage: window.SSO_TEST.cancelAutoRefresh()
     */
    cancelAutoRefresh: () => {
      cancelScheduledRefresh();
    },

    /**
     * Check if auto-refresh is scheduled
     * Usage: window.SSO_TEST.isAutoRefreshScheduled()
     */
    isAutoRefreshScheduled: () => {
      return {
        scheduled: autoRefreshTimeoutId !== null,
        retryCount: refreshRetryCount,
        online: navigator.onLine,
      };
    },

    /**
     * Reset retry count (for testing)
     * Usage: window.SSO_TEST.resetRetryCount()
     */
    resetRetryCount: () => {
      refreshRetryCount = 0;
    },
  };
}
// =============================================================================

export interface ISsoRefreshResult {
  idToken: string;
  accessToken: string;
  expiresAt: number;
  refreshToken?: string;
}

/**
 * Sets the SSO repository for token refresh operations.
 * Must be called after AppController initialization to break circular dependency.
 */
export const setSsoRepository = (repo: ISsoRepository) => {
  ssoRepository = repo;
};

/**
 * Attempts to refresh SSO tokens using the refresh token
 * @param refreshToken The refresh token to use
 * @param userId The user ID (used for username and sub in API request)
 * @returns New tokens or null if refresh failed
 */
const refreshSsoTokens = async (
  refreshToken: string,
  userId: string
): Promise<ISsoRefreshResult | null> => {
  if (!ssoRepository) {
    console.error("[SSO Refresh] Repository not initialized");
    return null;
  }

  if (!userId) {
    console.error("[SSO Refresh] User ID is required for token refresh");
    return null;
  }

  // Prevent concurrent refresh attempts
  if (isRefreshingToken && refreshPromise) {
    ssoDebug("⏳ Refresh already in progress, waiting...");
    return refreshPromise;
  }

  isRefreshingToken = true;
  ssoDebug("🚀 Starting refresh API call...");

  refreshPromise = (async () => {
    try {
      const isMicrosoftSso =
        localStorageProvider.get("microsoftSso") === "true";
      const type = isMicrosoftSso ? "microsoft" : "pcc";
      const username = isMicrosoftSso
        ? parseJwt(localStorageProvider.get("idToken")!)["cognito:username"] ??
          userId
        : userId;
      const response = await ssoRepository!.refreshToken(
        refreshToken,
        userId,
        username,
        type
      );

      ssoDebug(
        "📥 Refresh API response:",
        response ? "received" : "null/empty"
      );

      if (!response || !response.id_token) {
        ssoDebug("❌ No id_token in response");
        return null;
      }

      ssoDebug("✅ New tokens received!");
      ssoDebug("📝 Updating localStorage with new tokens...");

      // Update stored tokens
      localStorageProvider.set("idToken", response.id_token);
      if (response.access_token) {
        localStorageProvider.set("accessToken", response.access_token);
      }
      if (response.refresh_token) {
        ssoDebug("🔄 Refresh token rotated - storing new one");
        localStorageProvider.set("refreshToken", response.refresh_token);
      }

      // If running inside Android WebView, push refreshed tokens to native.
      try {
        if (
          typeof window !== "undefined" &&
          window.AndroidAuth?.setAuthTokens
        ) {
          const tokenData = parseJwt(response.id_token);
          const expSec: number | undefined = tokenData?.exp;
          const expiresAtMs =
            typeof expSec === "number" && expSec > 0
              ? expSec * 1000
              : undefined;
          window.AndroidAuth.setAuthTokens(
            JSON.stringify({
              idToken: response.id_token,
              accessToken: response.access_token,
              refreshToken: response.refresh_token,
              expiresAtMs,
            })
          );
        }
      } catch {
        // ignore
      }

      const tokenData = parseJwt(response.id_token);
      ssoDebug(
        "⏰ New token expires at:",
        new Date(tokenData.exp * 1000).toISOString()
      );

      return {
        idToken: response.id_token,
        accessToken:
          response.access_token ||
          localStorageProvider.get("accessToken") ||
          "",
        expiresAt: response.expires_at || tokenData.exp || 0,
        refreshToken: response.refresh_token,
      };
    } catch (error) {
      console.error("[SSO Refresh] Error refreshing token:", error);
      ssoDebug("💥 Refresh failed with error:", error);
      return null;
    } finally {
      isRefreshingToken = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ToDo: create independent type once service is update
export type IAuthServiceImplFactory = ReturnType<typeof AuthServiceImplFactory>;

export const AuthServiceImplFactory = ({
  authProvider,
  circadiaEngineProvider,
  electronEngineProvider,
}: {
  authProvider: IAuthProvider;
  circadiaEngineProvider: ICircadiaEngineProvider;
  electronEngineProvider: IElectronEngineProvider;
}) => {
  const isCircadiaEngine = circadiaEngineProvider.isWindowCircadia();
  const isElectronEngine = electronEngineProvider.isWindowElectron();

  const getCurrentSessionPayload = async () => {
    if (isElectronEngine) {
      const session = await electronEngineProvider.currentSession();
      return session.sessionPayload;
    } else {
      return (await authProvider.currentSession()).getIdToken().payload;
    }
  };

  const getCurrentUserInfo = () => {
    if (localStorageProvider.get("zpax") === "true") {
      return authProvider.currentUserInfo();
    }
    if (localStorageProvider.get("sso") === "true") {
      return authProvider.currentUserInfo();
    }
    if (isElectronEngine) {
      return electronEngineProvider.currentUserInfo();
    } else {
      return authProvider.currentUserInfo();
    }
  };

  const currentAuthenticatedUser = () =>
    authProvider.currentAuthenticatedUser();

  const login = async ({
    email,
    password,
    token,
    expires_at,
  }: LoginValues & { token?: string; expires_at?: number }) => {
    if (token) {
      return authProvider.ssoLogin({
        token,
        expires_at: expires_at ?? 0,
      });
    }

    if (isElectronEngine) {
      if (password === "") {
        const response = await electronEngineProvider.signInWithPasswordless({
          username: email,
        });

        if (response.status === "error") {
          throw response;
        }

        return response;
      } else {
        const response = await electronEngineProvider.signIn({
          username: email,
          password,
        });

        if (response.status === "error") {
          throw response;
        }

        return response;
      }
    } else {
      return authProvider.signIn(email, password);
    }
  };

  const ssoLogin = async (authParams: {
    token: string;
    expires_at: number;
  }) => {
    return authProvider.ssoLogin(authParams);
  };

  const forgotPassword = async ({ email }: IForgotPasswordValues) => {
    if (isElectronEngine) {
      return electronEngineProvider.forgotPassword({ username: email });
    } else {
      return authProvider.forgotPassword(email);
    }
  };

  const resetPassword = async ({
    email,
    code,
    password,
  }: IResetPasswordValues) => {
    if (isElectronEngine) {
      return electronEngineProvider.confirmPassword({
        username: email,
        code,
        password,
      });
    } else {
      return authProvider.resetPassword(email, code, password);
    }
  };

  const completeNewPasswordChallenge = async ({
    user,
    password,
    email,
  }: Partial<LoginValues> & {
    user?: User;
    password: string;
  }) => {
    if (isElectronEngine) {
      const response = await electronEngineProvider.completeNewPassword({
        password,
      });

      if (response.status === "error") {
        throw response;
      }

      return response;
    } else {
      return authProvider.completeNewPassword(user!, password, {
        email: email!,
      });
    }
  };

  const getAuthenticationToken = async (tokenParam?: string) => {
    if (tokenParam) return tokenParam;
    let session;

    // Android WebView note:
    // Auth is web-first (SSO/2FA) and tokens are pushed Web -> Android via AndroidAuth.setAuthTokens().
    // The web app should use its own localStorage tokens for API auth (no Android->Web token pulling here).

    // Handle Z-Pax tokens similar to SSO
    if (localStorageProvider.get("zpax") === "true") {
      const token = localStorageProvider.get("idToken");
      if (token) {
        try {
          const tokenData = parseJwt(token);

          // Check if token is expired
          if (tokenData.exp && tokenData.exp < Math.floor(Date.now() / 1000)) {
            // Clear Z-Pax tokens and logout the user
            localStorageProvider.remove("zpax");
            localStorageProvider.remove("idToken");
            localStorageProvider.remove("accessToken");
            localStorageProvider.remove("refreshToken");
            window.location.replace(window.location.origin + "/#/logout");
            return "";
          }

          return token;
        } catch (error) {
          // If token parsing fails, clear Z-Pax tokens and continue with normal auth
          console.error("Failed to parse Z-Pax token:", error);
          localStorageProvider.remove("zpax");
          localStorageProvider.remove("idToken");
          localStorageProvider.remove("accessToken");
          localStorageProvider.remove("refreshToken");
        }
      } else {
        // If zpax=true but no idToken, clear zpax flag and continue with normal auth
        localStorageProvider.remove("zpax");
      }
    }

    if (localStorageProvider.get("sso") === "true") {
      ssoDebug("🔐 SSO mode detected - checking token...");

      // First try to get token from localStorage (preferred for SSO)
      let token = localStorageProvider.get("idToken");

      // Fallback to currentAuthenticatedUser if no stored token
      if (!token) {
        ssoDebug(
          "📭 No token in localStorage, trying currentAuthenticatedUser..."
        );
        const currentUser = await currentAuthenticatedUser();
        token = (currentUser as any)?.token;
      }

      if (!token) {
        ssoDebug("❌ No token found anywhere");
        return "";
      }

      const tokenData = parseJwt(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const expiresAt = tokenData.exp || 0;
      const timeUntilExpiry = expiresAt - currentTime;
      const expiresIn =
        timeUntilExpiry > 0
          ? `${Math.floor(timeUntilExpiry / 60)}m ${timeUntilExpiry % 60}s`
          : "EXPIRED";

      ssoDebug("📋 Token info:", {
        expiresAt: new Date(expiresAt * 1000).toISOString(),
        currentTime: new Date(currentTime * 1000).toISOString(),
        expiresIn,
        bufferSeconds: TOKEN_REFRESH_BUFFER_SECONDS,
      });

      // Check if token is expired or about to expire (within buffer)
      const isExpiredOrExpiring =
        tokenData.exp &&
        tokenData.exp < currentTime + TOKEN_REFRESH_BUFFER_SECONDS;

      ssoDebug(
        "⏰ Token status:",
        isExpiredOrExpiring ? "NEEDS REFRESH" : "VALID"
      );

      if (isExpiredOrExpiring) {
        const refreshToken = localStorageProvider.get("refreshToken");

        ssoDebug("🔑 Refresh token available:", refreshToken ? "YES" : "NO");

        // Attempt to refresh if we have a refresh token
        if (refreshToken) {
          const userId = tokenData.sub;
          if (!userId) {
            ssoDebug("⚠️ No userId (sub) in token - cannot refresh");
          } else {
            ssoDebug(
              "🔄 Token expired or expiring soon, attempting refresh..."
            );
            ssoDebug("👤 User ID for refresh:", userId);
          }

          // Switch to SSO identity pool config for the entire refresh flow
          // (both the API call and the federatedSignIn need the SSO identity pool)
          if (amplifyProviderRef) {
            const type =
              localStorageProvider.get("microsoftSso") === "true"
                ? "microsoft_sso"
                : "sso";
            amplifyProviderRef.amplifyConfigure(type);
          }
          try {
            const newTokens = userId
              ? await refreshSsoTokens(refreshToken, userId)
              : null;

            if (newTokens) {
              ssoDebug("✅ Got new tokens, re-authenticating with Amplify...");
              // Re-authenticate with Amplify using new token
              try {
                await authProvider.ssoLogin({
                  token: newTokens.idToken,
                  expires_at: newTokens.expiresAt,
                });
                ssoDebug("🎉 Re-authentication successful!");
                return newTokens.idToken;
              } catch (error) {
                console.error(
                  "[SSO] Failed to re-authenticate after refresh:",
                  error
                );
                ssoDebug("💥 Re-authentication failed:", error);
              }
            } else {
              ssoDebug("❌ Refresh returned no tokens");
            }
          } finally {
            // Always switch back to default config
            if (amplifyProviderRef) {
              amplifyProviderRef.amplifyConfigure();
            }
          }
        } else {
          ssoDebug("⚠️ No refresh token available - cannot refresh");
        }

        // Refresh failed or no refresh token - check if actually expired
        if (tokenData.exp && tokenData.exp < currentTime) {
          console.warn(
            "[SSO] Token expired and refresh failed, logging out..."
          );
          ssoDebug("🚪 Token actually expired, forcing logout...");
          localStorageProvider.remove("sso");
          localStorageProvider.remove("accessToken");
          localStorageProvider.remove("refreshToken");
          localStorageProvider.remove("idToken");
          localStorageProvider.remove("userInfoUpdated");
          // Logout the user
          window.location.replace(window.location.origin + "/#/logout");
          return "";
        } else {
          ssoDebug("⏳ Token not yet expired, continuing with current token");
        }
      }

      // For testing: Uncomment the line below to simulate token expiration
      // if (tokenData.exp) {
      //
      //   window.location.replace(window.location.origin + "/#/logout");
      //   return "";
      // }

      ssoDebug("✅ Returning valid token");
      // Check if token is expired
      if (tokenData.exp && tokenData.exp < Math.floor(Date.now() / 1000)) {
        localStorageProvider.remove("sso");
        localStorageProvider.remove("microsoftSso");
        localStorageProvider.remove("accessToken");
        localStorageProvider.remove("refreshToken");
        localStorageProvider.remove("idToken");
        localStorageProvider.remove("userInfoUpdated");
        // Also clear MSAL cache to prevent stale Graph tokens
        clearMsalCache();
        localStorage.removeItem("msAccessToken");
        // Logout the user
        window.location.replace(window.location.origin + "/#/logout");
        return "";
      }

      return token;
    }
    try {
      if (isElectronEngine) {
        const tokens = await electronEngineProvider.retrieveTokens();
        session = tokens.idToken;
      } else {
        session =
          isCircadiaEngine &&
          circadiaEngineProvider.intfv >= 5 &&
          circadiaEngineProvider.isIOS
            ? (await circadiaEngineProvider.cognitoTokens()).IdToken
            : (await authProvider.currentSession()).getIdToken().getJwtToken();
      }
    } catch {
      return "";
    }
    return session;
  };

  const getAuthenticationAccessToken = async () => {
    if (localStorageProvider.get("zpax") === "true") {
      const accessToken = localStorageProvider.get("accessToken");
      // Return access token even if null/undefined - this is normal for Z-Pax
      return accessToken;
    }
    if (localStorageProvider.get("sso") === "true") {
      return localStorageProvider.get("accessToken");
    }
    let session;
    try {
      if (isElectronEngine) {
        const tokens = await electronEngineProvider.retrieveTokens();
        session = tokens.accessToken;
      } else {
        session =
          isCircadiaEngine &&
          circadiaEngineProvider.intfv >= 5 &&
          circadiaEngineProvider.isIOS
            ? (await circadiaEngineProvider.cognitoTokens()).AccessToken
            : (await authProvider.currentSession())
                .getAccessToken()
                .getJwtToken();
      }
    } catch {
      return "";
    }
    return session;
  };

  const completeSMSMFAChallenge = async ({
    user,
    code,
    typeOfChallenge = "SMS_MFA",
  }: SMSMFAValues) => {
    if (isElectronEngine) {
      if (typeOfChallenge === "CUSTOM_CHALLENGE") {
        const response = await electronEngineProvider.confirmPasswordlessMFA({
          code,
        });
        if (response.status === "error") {
          throw response;
        }
        return response;
      } else {
        const response = await electronEngineProvider.confirmMFA({ code });
        if (response.status === "error") {
          throw response;
        }

        return response;
      }
    } else {
      return authProvider.confirmSignIn(user!, code, typeOfChallenge);
    }
  };

  const changePassword = ({
    user,
    oldPassword,
    newPassword,
  }: IChangePasswordUser) => {
    if (isElectronEngine) {
      return electronEngineProvider.changePassword({
        oldPassword,
        newPassword,
      });
    } else {
      return authProvider.changePassword(user!, oldPassword, newPassword);
    }
  };

  const checkPassword = async ({ oldPassword }: ICheckPassword) => {
    return electronEngineProvider.checkPassword({
      oldPassword,
    });
  };

  const logout = () => {
    if (isElectronEngine) {
      return electronEngineProvider.signOut();
    } else {
      return authProvider.signOut();
    }
  };

  const createSSPin = async ({ pin }: ICreateSSPin) => {
    if (isElectronEngine) {
      return electronEngineProvider.setupPin(pin);
    }
    if (
      isCircadiaEngine &&
      circadiaEngineProvider.intfv >= 5 &&
      circadiaEngineProvider.isIOS
    ) {
      const jwtTokenFromEngine = await circadiaEngineProvider.cognitoTokens();
      return circadiaEngineProvider.changeSSPin(
        jwtTokenFromEngine.IdToken,
        pin
      );
    } else {
      const token = await getAuthenticationToken();
      return circadiaEngineProvider.createSSPin(token, pin);
    }
  };

  const saveSSValue = ({ email, key, value }: ISaveSSValue) => {
    return circadiaEngineProvider.saveSSValue(email, key, value);
  };

  const loadSSValue = ({ email, key, value }: ILoadSSValue) => {
    return circadiaEngineProvider.loadSSValue(email, key, value);
  };

  const cognitoProvidePin = (pin: string) => {
    return circadiaEngineProvider.cognitoProvidePin(pin);
  };

  const loadSSValueBio = ({ email, key }: ILoadSSValueBio) => {
    return circadiaEngineProvider.loadSSValueBio(email, key);
  };

  const changeSSPin = async ({ pin }: IChangeSSPin) => {
    const token = await getAuthenticationToken();
    return circadiaEngineProvider.changeSSPin(token, pin);
  };

  const authPreFlight = async (payload: ISsoAuthPreFlightRequest) => {
    return ssoRepository?.authPreFlight(payload);
  };

  return {
    changePassword,
    completeNewPasswordChallenge,
    completeSMSMFAChallenge,
    currentAuthenticatedUser,
    getAuthenticationToken,
    getCurrentSessionPayload,
    getCurrentUserInfo,
    login,
    ssoLogin,
    logout,
    createSSPin,
    saveSSValue,
    loadSSValue,
    loadSSValueBio,
    changeSSPin,
    getAuthenticationAccessToken,
    cognitoProvidePin,
    forgotPassword,
    resetPassword,
    checkPassword,
    authPreFlight,
  };
};
